import { useRef, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Send,
  Paperclip,
  Pencil,
  Trash2,
  Check,
  X,
  Copy,
  FileText,
  MoreVertical,
  MessagesSquare,
  Search,
  Plus,
  UserPlus,
} from "lucide-react";
import { formatTime } from "../../utils/formatters";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatSize = (b) =>
  b < 1024 ? `${b} Б` : b < 1048576 ? `${(b / 1024).toFixed(1)} КБ` : `${(b / 1048576).toFixed(1)} МБ`;

const ChatTab = ({
  conversations,
  selectedConversation,
  chatMessages,
  newMessage,
  setNewMessage,
  onSelectConversation,
  onSendMessage,
  onRefresh,
  token,
  regularUsers = [],
}) => {
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [contextMenu, setContextMenu] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [previewImage, setPreviewImage] = useState(null);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [newChatSearch, setNewChatSearch] = useState("");

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  useEffect(() => {
    const handler = () => setContextMenu(null);
    if (contextMenu) window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, [contextMenu]);

  const refresh = () => {
    if (selectedConversation && onRefresh) onRefresh(selectedConversation.user_id);
  };

  const handleEdit = async (id) => {
    if (!editText.trim()) return;
    try {
      await axios.put(
        `${API}/mnro/chat/messages/${id}`,
        { message: editText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingId(null);
      setEditText("");
      refresh();
      toast.success("Сообщение обновлено");
    } catch {
      toast.error("Ошибка редактирования");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/mnro/chat/messages/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      refresh();
      toast.success("Сообщение удалено");
    } catch {
      toast.error("Ошибка удаления");
    }
  };

  const handleCopy = (text) => {
    if (!text) {
      toast.info("Нечего копировать");
      return;
    }
    navigator.clipboard?.writeText(text);
    toast.success("Скопировано");
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedConversation) return;

    const ALLOWED = ["jpg","jpeg","png","gif","webp","bmp","pdf","doc","docx","xls","xlsx","txt","csv","zip","rar"];
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    if (!ALLOWED.includes(ext)) {
      toast.error(`Тип «.${ext}» не поддерживается.`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(`Файл слишком большой (${(file.size/1048576).toFixed(1)} МБ). Максимум 10 МБ.`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    try {
      await axios.post(
        `${API}/mnro/chat/upload/${selectedConversation.user_id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      refresh();
      toast.success("Файл отправлен");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ошибка загрузки");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleStartConversation = (user) => {
    // Find existing conversation or create a virtual one
    const existing = conversations.find((c) => c.user_id === user.id);
    if (existing) {
      onSelectConversation(existing);
    } else {
      // Virtual conversation — first sent message will materialize it server-side
      const virtual = {
        user_id: user.id,
        user_name: user.name,
        user_email: user.email,
        client_id: user.client_id || "",
        unread_count: 0,
        last_message: "",
        last_message_time: new Date().toISOString(),
      };
      onSelectConversation(virtual);
    }
    setShowNewChatModal(false);
    setNewChatSearch("");
  };

  const handleContextMenu = (e, msg) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, msg });
  };

  const filteredConversations = conversations.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      c.user_name?.toLowerCase().includes(q) ||
      c.user_email?.toLowerCase().includes(q) ||
      c.last_message?.toLowerCase().includes(q) ||
      c.user_id?.toLowerCase().includes(q) ||
      c.client_id?.toLowerCase().includes(q)
    );
  });

  // Users that don't have an active conversation yet — candidates for "new chat"
  const usersForNewChat = regularUsers.filter((u) => {
    const q = newChatSearch.toLowerCase().trim();
    if (!q) return true;
    return (
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.client_id?.toLowerCase().includes(q) ||
      u.id?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="grid md:grid-cols-[320px_1fr] gap-4 h-[calc(100vh-220px)]">
      {/* Conversations List */}
      <div className="bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-[#1F1F1F]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-white flex items-center gap-2">
              <MessagesSquare size={18} className="text-[#EDEDED]" />
              Диалоги
            </h3>
            <button
              onClick={() => setShowNewChatModal(true)}
              className="p-1.5 rounded-lg bg-white/10 text-[#EDEDED] hover:bg-[#1A1A1A] hover:text-white transition-colors"
              title="Новый диалог"
              data-testid="new-chat-btn"
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск по ID, имени, email..."
              className="w-full bg-[#1A1A1A] border border-[#1F1F1F] rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-[#71717A] focus:border-white focus:outline-none"
              data-testid="admin-chat-search"
            />
          </div>
        </div>
        <div className="overflow-y-auto flex-1">
          {filteredConversations.length === 0 ? (
            <div className="p-6 text-[#71717A] text-center text-sm">Нет диалогов</div>
          ) : (
            filteredConversations.map((conv) => {
              const active = selectedConversation?.user_id === conv.user_id;
              return (
                <button
                  key={conv.user_id}
                  onClick={() => onSelectConversation(conv)}
                  className={`w-full p-3 text-left border-b border-[#1F1F1F] transition-colors ${
                    active ? "bg-white/10 border-l-2 border-l-white" : "hover:bg-[#15151A]"
                  }`}
                  data-testid={`conversation-${conv.user_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                        active ? "bg-[#FF8A1E] text-white" : "bg-[#1F1F1F] text-[#EDEDED]"
                      }`}
                    >
                      {conv.user_name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex justify-between items-start gap-2">
                        <span className="font-medium text-white text-sm truncate">{conv.user_name}</span>
                        {conv.unread_count > 0 && (
                          <span className="px-1.5 min-w-[20px] h-5 bg-[#FF8A1E] rounded-full text-white text-[10px] font-bold flex items-center justify-center">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                      <div className="text-[#A1A1AA] text-xs truncate">{conv.last_message || "Файл / вложение"}</div>
                      <div className="text-[#52525B] text-[10px] mt-0.5">{formatTime(conv.last_message_time)}</div>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] flex flex-col overflow-hidden">
        {selectedConversation ? (
          <>
            {/* Header */}
            <div className="p-4 border-b border-[#1F1F1F] bg-[#1A1A1A] flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 text-[#EDEDED] flex items-center justify-center font-bold">
                {selectedConversation.user_name?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-white truncate">{selectedConversation.user_name}</div>
                <div className="text-[#A1A1AA] text-xs truncate">{selectedConversation.user_email}</div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-[#000000] to-[#0A0A0A]">
              {chatMessages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-[#71717A] gap-2">
                  <MessagesSquare size={36} className="opacity-30" />
                  <p className="text-sm">Сообщений пока нет</p>
                </div>
              ) : (
                chatMessages.map((msg) => {
                  const isAdmin = msg.sender === "admin";
                  const isEditing = editingId === msg.id;
                  return (
                    <div key={msg.id} className={`flex group ${isAdmin ? "justify-end" : "justify-start"}`}>
                      <div className="max-w-[75%] relative">
                        {isEditing ? (
                          <div className="flex items-end gap-2">
                            <input
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleEdit(msg.id);
                                if (e.key === "Escape") setEditingId(null);
                              }}
                              className="flex-1 bg-[#1A1A1A] border border-white/60 rounded-lg px-3 py-2 text-white text-sm focus:outline-none min-w-[200px]"
                              autoFocus
                              data-testid="admin-edit-input"
                            />
                            <button
                              onClick={() => handleEdit(msg.id)}
                              className="p-1.5 text-[#EDEDED] hover:text-white transition-colors"
                              data-testid="admin-edit-confirm"
                            >
                              <Check size={16} />
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="p-1.5 text-[#A1A1AA] hover:text-white transition-colors"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <>
                            {/* Attachments */}
                            {msg.attachments?.map((att, i) => (
                              <div key={i} className="mb-1">
                                {att.is_image ? (
                                  <button
                                    type="button"
                                    onClick={() => setPreviewImage(`${API}/chat/files/${att.filename}`)}
                                    onContextMenu={(e) => handleContextMenu(e, msg)}
                                    className="block group/img"
                                  >
                                    <img
                                      src={`${API}/chat/files/${att.filename}`}
                                      alt={att.original_name}
                                      className="max-w-[280px] rounded-lg border border-[#1F1F1F] max-h-56 object-cover hover:opacity-90 transition-opacity cursor-zoom-in"
                                    />
                                  </button>
                                ) : (
                                  <a
                                    href={`${API}/chat/files/${att.filename}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    onContextMenu={(e) => handleContextMenu(e, msg)}
                                    className="flex items-center gap-2 bg-[#1A1A1A] rounded-lg px-3 py-2 border border-[#1F1F1F] hover:border-[#2E2E2E]/40 transition-colors max-w-[280px]"
                                  >
                                    <FileText size={18} className="text-[#EDEDED] flex-shrink-0" />
                                    <div className="min-w-0">
                                      <p className="text-white text-sm truncate">{att.original_name}</p>
                                      <p className="text-[#71717A] text-xs">{formatSize(att.size)}</p>
                                    </div>
                                  </a>
                                )}
                              </div>
                            ))}

                            {/* Text bubble */}
                            {msg.message && (
                              <div
                                onContextMenu={(e) => handleContextMenu(e, msg)}
                                className={`relative rounded-2xl px-4 py-2.5 text-sm break-words whitespace-pre-wrap ${
                                  isAdmin
                                    ? "bg-[#FF8A1E] text-white rounded-tr-sm"
                                    : "bg-[#1A1A1A] text-white rounded-tl-sm border border-[#1F1F1F]"
                                }`}
                              >
                                {msg.message}
                              </div>
                            )}

                            {/* Meta + actions */}
                            <div
                              className={`flex items-center gap-2 mt-1 text-[10px] text-[#71717A] ${
                                isAdmin ? "justify-end mr-1" : "ml-1"
                              }`}
                            >
                              {msg.edited && <span className="italic">ред.</span>}
                              <span>{formatTime(msg.created_at)}</span>
                              <button
                                onClick={(e) => handleContextMenu(e, msg)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-[#1F1F1F] rounded"
                                title="Действия"
                                data-testid={`msg-actions-${msg.id}`}
                              >
                                <MoreVertical size={12} />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form
              onSubmit={onSendMessage}
              className="p-3 border-t border-[#1F1F1F] bg-[#1A1A1A] flex items-center gap-2"
            >
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="p-2.5 text-[#A1A1AA] hover:text-[#EDEDED] transition-colors disabled:opacity-50"
                data-testid="admin-attach-btn"
                title="Прикрепить файл"
              >
                <Paperclip size={20} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileUpload}
                accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip,.rar"
              />
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={uploading ? "Загрузка..." : "Напишите ответ..."}
                disabled={uploading}
                className="flex-1 bg-[#1A1A1A] border border-[#1F1F1F] rounded-full px-4 py-2.5 text-white text-sm placeholder-[#71717A] focus:border-white focus:outline-none disabled:opacity-50"
                data-testid="admin-chat-input"
              />
              <button
                type="submit"
                disabled={!newMessage.trim() || uploading}
                className="w-10 h-10 rounded-full bg-white hover:bg-[#E5E5E5] disabled:opacity-40 flex items-center justify-center transition-colors"
                data-testid="admin-send-btn"
              >
                <Send size={16} className="text-white" />
              </button>
            </form>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-[#71717A] gap-3">
            <MessagesSquare size={48} className="opacity-30" />
            <p>Выберите диалог слева</p>
          </div>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed z-[60] bg-[#1A1A1A] border border-[#1F1F1F] rounded-lg shadow-2xl py-1 min-w-[160px]"
          style={{ top: Math.min(contextMenu.y, window.innerHeight - 160), left: Math.min(contextMenu.x, window.innerWidth - 180) }}
          data-testid="admin-context-menu"
        >
          {contextMenu.msg.message && (
            <button
              onClick={() => {
                handleCopy(contextMenu.msg.message);
                setContextMenu(null);
              }}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-white hover:bg-[#1A1A1A]/10 transition-colors"
              data-testid="ctx-copy-btn"
            >
              <Copy size={14} /> Копировать
            </button>
          )}
          {contextMenu.msg.message && contextMenu.msg.sender === "admin" && (
            <button
              onClick={() => {
                setEditingId(contextMenu.msg.id);
                setEditText(contextMenu.msg.message);
                setContextMenu(null);
              }}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-white hover:bg-[#1A1A1A]/10 transition-colors"
              data-testid="ctx-edit-btn"
            >
              <Pencil size={14} /> Редактировать
            </button>
          )}
          <button
            onClick={() => {
              handleDelete(contextMenu.msg.id);
              setContextMenu(null);
            }}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-[#F87171] hover:bg-[#F87171]/10 transition-colors"
            data-testid="ctx-delete-btn"
          >
            <Trash2 size={14} /> Удалить
          </button>
        </div>
      )}

      {/* Image preview lightbox */}
      {previewImage && (
        <div
          className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setPreviewImage(null)}
          data-testid="image-preview-overlay"
        >
          <img src={previewImage} alt="preview" className="max-w-full max-h-full object-contain rounded-lg" />
          <button
            onClick={() => setPreviewImage(null)}
            className="absolute top-4 right-4 p-2 bg-[#1A1A1A]/80 hover:bg-[#1A1A1A] text-white rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>
      )}

      {/* New conversation modal */}
      {showNewChatModal && (
        <div
          className="fixed inset-0 z-[80] bg-black/70-sm flex items-center justify-center p-4"
          onClick={() => setShowNewChatModal(false)}
          data-testid="new-chat-modal"
        >
          <div
            className="w-full max-w-md bg-[#0A0A0A] border border-[#1F1F1F] rounded-xl overflow-hidden shadow-2xl flex flex-col max-h-[80vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-[#1F1F1F] flex items-center justify-between">
              <h3 className="font-bold text-white flex items-center gap-2">
                <UserPlus size={18} className="text-[#EDEDED]" />
                Новый диалог
              </h3>
              <button
                onClick={() => setShowNewChatModal(false)}
                className="p-1 text-[#A1A1AA] hover:text-white"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-4 border-b border-[#1F1F1F]">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
                <input
                  type="text"
                  value={newChatSearch}
                  onChange={(e) => setNewChatSearch(e.target.value)}
                  placeholder="Поиск по ID, имени, email..."
                  className="w-full bg-[#1A1A1A] border border-[#1F1F1F] rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-[#71717A] focus:border-white focus:outline-none"
                  autoFocus
                  data-testid="new-chat-search-input"
                />
              </div>
              <div className="mt-2 text-xs text-[#71717A]">
                Найдено: {usersForNewChat.length} из {regularUsers.length}
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {usersForNewChat.length === 0 ? (
                <div className="p-6 text-[#71717A] text-center text-sm">Пользователи не найдены</div>
              ) : (
                usersForNewChat.slice(0, 100).map((u) => {
                  const hasConv = conversations.some((c) => c.user_id === u.id);
                  return (
                    <button
                      key={u.id}
                      onClick={() => handleStartConversation(u)}
                      className="w-full p-3 text-left border-b border-[#1F1F1F] hover:bg-[#15151A] transition-colors flex items-center gap-3"
                      data-testid={`new-chat-user-${u.id}`}
                    >
                      <div className="w-9 h-9 rounded-full bg-white/20 text-[#EDEDED] flex items-center justify-center font-bold text-sm flex-shrink-0">
                        {u.name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-white text-sm truncate">{u.name}</span>
                          {u.client_id && (
                            <span className="text-[10px] text-[#EDEDED] bg-white/10 px-1.5 py-0.5 rounded">
                              #{u.client_id}
                            </span>
                          )}
                        </div>
                        <div className="text-[#A1A1AA] text-xs truncate">{u.email}</div>
                      </div>
                      {hasConv && (
                        <span className="text-[10px] text-[#71717A] flex-shrink-0">диалог есть</span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatTab;
